import { h } from 'preact';
import { useState } from 'preact/hooks';

type ErrorClass = 'auth' | 'rate-limit' | 'overload' | 'other';

interface Classified {
  errorClass: ErrorClass;
  title: string;
  summary: string;
  recoveryAction: 'open-settings' | 'retry' | 'switch-provider' | null;
  recoveryLabel: string | null;
}

function classifyError(text: string, metadata?: Record<string, any>): Classified {
  if (metadata?.errorClass) {
    const cls = metadata.errorClass as ErrorClass;
    if (cls === 'auth') return { errorClass: 'auth', title: 'Authentication failed', summary: 'The AI provider rejected your credentials. Update your API key in Settings.', recoveryAction: 'open-settings', recoveryLabel: 'Update API key' };
    if (cls === 'rate-limit') return { errorClass: 'rate-limit', title: 'Rate limit reached', summary: 'Too many requests in a short time. Wait a moment, then try again.', recoveryAction: 'retry', recoveryLabel: 'Try again' };
    if (cls === 'overload') return { errorClass: 'overload', title: 'Provider overloaded', summary: 'The AI provider is under heavy load. Switch to another model or try again shortly.', recoveryAction: 'switch-provider', recoveryLabel: 'Switch to Gemini' };
  }
  const lower = text.toLowerCase();
  if (/401|failed to authenticate|authentication.error|invalid.{0,10}(api.)?key|invalid_api_key/.test(lower)) {
    return { errorClass: 'auth', title: 'Authentication failed', summary: 'The AI provider rejected your credentials. Update your API key in Settings.', recoveryAction: 'open-settings', recoveryLabel: 'Update API key' };
  }
  if (/rate.?limit|429|too many request/.test(lower)) {
    return { errorClass: 'rate-limit', title: 'Rate limit reached', summary: 'Too many requests in a short time. Wait a moment, then try again.', recoveryAction: 'retry', recoveryLabel: 'Try again' };
  }
  if (/overload|529|\bcapacity\b/.test(lower)) {
    return { errorClass: 'overload', title: 'Provider overloaded', summary: 'The AI provider is under heavy load. Switch to another model or try again shortly.', recoveryAction: 'switch-provider', recoveryLabel: 'Switch to Gemini' };
  }
  return { errorClass: 'other', title: 'Request failed', summary: text.slice(0, 140), recoveryAction: null, recoveryLabel: null };
}

interface ErrorCardProps {
  text: string;
  metadata?: Record<string, any>;
  onOpenSettings?: () => void;
  onSwitchToGemini?: () => void;
}

export function ErrorCard({ text, metadata, onOpenSettings, onSwitchToGemini }: ErrorCardProps) {
  const [copied, setCopied] = useState(false);
  const { errorClass, title, summary, recoveryAction, recoveryLabel } = classifyError(text, metadata);

  const handleRecovery = () => {
    if (recoveryAction === 'open-settings') onOpenSettings?.();
    else if (recoveryAction === 'switch-provider') onSwitchToGemini?.();
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <div class={`error-card error-card--${errorClass}`} aria-live="polite" role="alert">
      <div class="error-card__icon" aria-hidden="true">⚠</div>
      <div class="error-card__body">
        <div class="error-card__title">{title}</div>
        <div class="error-card__summary">{summary}</div>
      </div>
      <div class="error-card__actions">
        {recoveryLabel && (recoveryAction === 'open-settings' ? onOpenSettings : recoveryAction === 'switch-provider' ? onSwitchToGemini : true) && (
          <button class="error-card__cta" onClick={handleRecovery}>
            {recoveryLabel}
          </button>
        )}
        <button class="error-card__copy" onClick={handleCopy} aria-label="Copy error details">
          {copied ? '✓ Copied' : 'Copy details'}
        </button>
      </div>
    </div>
  );
}

/** True when an assistant message text + optional metadata signal a structured provider error. */
export function isErrorMessage(text: string, metadata?: Record<string, any>): boolean {
  if (metadata?.kind === 'error') return true;
  const t = text.toLowerCase();
  return (
    /failed to authenticate\. api error:/i.test(t) ||
    /api error:.*40[19]/.test(t) ||
    /authentication.error/.test(t) ||
    /rate.?limit.*error|error.*rate.?limit/.test(t) ||
    /overloaded.*error|error.*overload/.test(t)
  );
}
